'use client';

import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// Import Swiper React components
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import Image from 'next/image';

// Import Swiper styles
import 'swiper/css';
import { useLocale } from '@/components/providers/context/locale-context';
import { JContainer } from '@/components/ui/layout';

function Arrow(props: Readonly<{ disabled: boolean; left?: boolean }>) {
    const swiper = useSwiper();

    return (
        <svg
            onClick={() => {
                props.left ? swiper.slidePrev() : swiper.slideNext();
            }}
            className={clsx(
                `h-[40px] w-[40px] cursor-pointer sm:h-[48px] sm:w-[48px]`,
                { 'fill-[#4C4C4C]': props.disabled },
            )}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
        >
            <circle
                cx="24"
                cy="24"
                r="24"
                fill="#E4E0E1"
                className={clsx('', {
                    'fill-[#242427]': props.disabled,
                })}
            />
            {props.left && (
                <path d="M17.1161 23.1161C16.628 23.6043 16.628 24.3957 17.1161 24.8839L25.0711 32.8388C25.5592 33.327 26.3507 33.327 26.8388 32.8388C27.327 32.3507 27.327 31.5592 26.8388 31.0711L19.7678 24L26.8388 16.9289C27.327 16.4408 27.327 15.6493 26.8388 15.1612C26.3507 14.673 25.5592 14.673 25.0711 15.1612L17.1161 23.1161ZM19 22.75L18 22.75L18 25.25L19 25.25L19 22.75Z" />
            )}
            {!props.left && (
                <path d="M29.8839 24.8839C30.372 24.3957 30.372 23.6043 29.8839 23.1161L21.9289 15.1612C21.4408 14.673 20.6493 14.673 20.1612 15.1612C19.673 15.6493 19.673 16.4408 20.1612 16.9289L27.2322 24L20.1612 31.0711C19.673 31.5592 19.673 32.3507 20.1612 32.8388C20.6493 33.327 21.4408 33.327 21.9289 32.8388L29.8839 24.8839ZM28 25.25L29 25.25L29 22.75L28 22.75L28 25.25Z" />
            )}
        </svg>
    );
}

const minSlideWidth = 1350;

function TeamMembers() {
    const { t } = useTranslation('team');
    const locale = useLocale();
    const [currentSlide, setCurrentSlide] = useState(0);

    const teamMembers: {
        id: string;
        name: string;
        title: string;
        imageUrl: string;
        imageDesc: string;
        motto: string;
    }[] = t('members', { returnObjects: true }) as {
        id: string;
        name: string;
        title: string;
        imageUrl: string;
        imageDesc: string;
        motto: string;
    }[];

    return (
        <JContainer
            size="lg"
            padding="lg"
            className="flex items-center justify-center"
        >
            <div className="flex w-full flex-col items-center justify-center">
                <Swiper
                    className="w-full "
                    spaceBetween={32}
                    slidesPerView={'auto'}
                    onSlideChange={(s) => {
                        setCurrentSlide(s.activeIndex);
                    }}
                >
                    {teamMembers.map((teamMember, index) => (
                        <SwiperSlide
                            id={'data-problems-' + teamMember.id}
                            key={teamMember.id}
                            className={clsx(`flex max-w-[240px] flex-col`)}
                        >
                            <div className={clsx('flex w-full flex-col')}>
                                <div className="flex min-h-[320px] w-full overflow-hidden">
                                    <Image
                                        alt={teamMember.imageDesc}
                                        title={teamMember.name}
                                        sizes="100vw"
                                        width={240}
                                        height={320}
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            objectFit: 'cover',
                                            objectPosition: 'center',
                                            transition: 'none', // 添加这一行来取消动画效果
                                            userSelect: 'none', // 添加这一行来禁止选中元素的内容
                                        }}
                                        src={teamMember.imageUrl}
                                    />
                                </div>
                                <div
                                    className={clsx(
                                        'j-h5 mt-[16px] text-[#FFFFFF]',
                                    )}
                                >
                                    {teamMember.name}
                                </div>
                                <div
                                    className={clsx(
                                        'j-t2 mt-[4px] text-[#FFFFFF]',
                                    )}
                                >
                                    {teamMember.title}
                                </div>

                                <div
                                    className={clsx(
                                        'j-t2 mt-[20px] text-[#C8C8C8]',
                                    )}
                                >
                                    {teamMember.motto}
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}

                    <div className="mt-[32px] flex w-full flex-row justify-end gap-[8px] sm:gap-[16px]">
                        <Arrow left disabled={currentSlide === 0} />

                        <Arrow
                            disabled={currentSlide === teamMembers.length - 1}
                        />
                    </div>
                </Swiper>
            </div>
        </JContainer>
    );
}

export default TeamMembers;
